/**
 * ReceiptPrinter — Generates HTML receipt and opens print dialog.
 * Desktop-ready: same HTML template works for browser window.print() and
 * future Electron ESC/POS thermal printer integration.
 */
import React from "react";
import { Dialog, DialogContent, Box, Typography, Button, IconButton, Divider, alpha } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";

export interface ReceiptItem {
  name: string;
  qty: number;
  unit?: string;
  price: number;
  total: number;
  is_weight_based?: boolean;
}

export interface ReceiptData {
  invoiceNo: string;
  date: string;
  cashier?: string;
  customer?: string;
  items: ReceiptItem[];
  subTotal: number;
  discount: number;
  taxLabel: string;
  taxAmount: number;
  grandTotal: number;
  paymentMethod: string;
  amountPaid: number;
  change: number;
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
}

interface ReceiptPrinterProps {
  open: boolean;
  onClose: () => void;
  data: ReceiptData | null;
}

const buildReceiptHTML = (r: ReceiptData): string => {
  const itemRows = r.items.map(item => {
    const qtyDisplay = item.is_weight_based
      ? `${item.qty.toFixed(3)} ${item.unit || "kg"}`
      : String(item.qty);
    return `
      <tr>
        <td style="text-align:left;padding:2px 0;font-size:12px;">${item.name}</td>
        <td style="text-align:center;padding:2px 0;font-size:12px;">${qtyDisplay}</td>
        <td style="text-align:right;padding:2px 0;font-size:12px;">$${item.price.toFixed(2)}</td>
        <td style="text-align:right;padding:2px 0;font-size:12px;">$${item.total.toFixed(2)}</td>
      </tr>`;
  }).join("");

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>Receipt ${r.invoiceNo}</title>
<style>
  @page { margin: 0; size: 80mm auto; }
  body { font-family: 'Courier New', monospace; width: 76mm; margin: 2mm auto; color: #000; }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  .line { border-top: 1px dashed #000; margin: 6px 0; }
  .row { display: flex; justify-content: space-between; font-size: 12px; padding: 1px 0; }
  .row-bold { display: flex; justify-content: space-between; font-size: 13px; font-weight: bold; padding: 2px 0; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: 11px; border-bottom: 1px solid #000; padding: 3px 0; }
  th:nth-child(2) { text-align: center; }
  th:nth-child(3), th:nth-child(4) { text-align: right; }
</style>
</head><body>
  <div class="center bold" style="font-size:16px;margin-bottom:2px;">${r.companyName || "POS Store"}</div>
  ${r.companyAddress ? `<div class="center" style="font-size:10px;">${r.companyAddress}</div>` : ""}
  ${r.companyPhone ? `<div class="center" style="font-size:10px;">Tel: ${r.companyPhone}</div>` : ""}
  <div class="line"></div>
  <div class="row"><span>Invoice: ${r.invoiceNo}</span><span>${r.date}</span></div>
  ${r.cashier ? `<div class="row"><span>Cashier: ${r.cashier}</span></div>` : ""}
  ${r.customer ? `<div class="row"><span>Customer: ${r.customer}</span></div>` : ""}
  <div class="line"></div>
  <table>
    <thead><tr><th>Item</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead>
    <tbody>${itemRows}</tbody>
  </table>
  <div class="line"></div>
  <div class="row"><span>Subtotal</span><span>$${r.subTotal.toFixed(2)}</span></div>
  ${r.discount > 0 ? `<div class="row"><span>Discount</span><span>-$${r.discount.toFixed(2)}</span></div>` : ""}
  <div class="row"><span>${r.taxLabel}</span><span>$${r.taxAmount.toFixed(2)}</span></div>
  <div class="line"></div>
  <div class="row-bold"><span>TOTAL</span><span>$${r.grandTotal.toFixed(2)}</span></div>
  <div class="line"></div>
  <div class="row"><span>Payment: ${r.paymentMethod}</span></div>
  <div class="row"><span>Paid</span><span>$${r.amountPaid.toFixed(2)}</span></div>
  ${r.change > 0 ? `<div class="row-bold"><span>Change</span><span>$${r.change.toFixed(2)}</span></div>` : ""}
  <div class="line"></div>
  <div class="center" style="font-size:11px;margin-top:8px;">Thank you for your purchase!</div>
  <div class="center" style="font-size:10px;color:#888;margin-top:4px;">Powered by CaCart POS</div>
</body></html>`;
};

/**
 * Print receipt via browser window.
 * Reusable — can be called from ReceiptPrinter dialog OR from order history reprint.
 */
export const printReceiptFromData = (data: ReceiptData): void => {
  const html = buildReceiptHTML(data);
  const win = window.open("", "_blank", "width=320,height=600");
  if (!win) {
    alert("Popup blocked — please allow popups for this site to print receipts.");
    return;
  }
  win.document.write(html);
  win.document.close();
  // Wait for images/fonts to render, then print, then close
  win.onload = () => {
    win.focus();
    win.print();
    // Close after print dialog resolves (user prints or cancels)
    win.onafterprint = () => win.close();
  };
  // Fallback: if onload doesn't fire (some browsers), use timeout
  setTimeout(() => {
    try { win.focus(); win.print(); } catch {}
  }, 500);
};

const ReceiptPrinter: React.FC<ReceiptPrinterProps> = ({ open, onClose, data }) => {
  if (!data) return null;

  const handlePrint = () => printReceiptFromData(data);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Typography fontSize={15} fontWeight={700} color="#fff">Receipt Preview</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.5)" }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Receipt preview */}
        <Box sx={{ p: 3, bgcolor: "#fafafa", fontFamily: "'Courier New', monospace", fontSize: 12 }}>
          <Box sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", borderRadius: 2, p: 2.5, maxWidth: 300, mx: "auto", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <Typography textAlign="center" fontWeight={700} fontSize={16} sx={{ fontFamily: "inherit" }}>
              {data.companyName || "POS Store"}
            </Typography>
            {data.companyAddress && (
              <Typography textAlign="center" fontSize={10} color="#666" sx={{ fontFamily: "inherit" }}>{data.companyAddress}</Typography>
            )}
            <Divider sx={{ my: 1, borderStyle: "dashed" }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
              <span>{data.invoiceNo}</span><span>{data.date}</span>
            </Box>
            {data.customer && <Typography fontSize={11}>Customer: {data.customer}</Typography>}
            <Divider sx={{ my: 1, borderStyle: "dashed" }} />

            {data.items.map((item, i) => (
              <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: 0.25, fontSize: 11 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography fontSize={11} sx={{ fontFamily: "inherit" }}>{item.name}</Typography>
                  <Typography fontSize={10} color="#888" sx={{ fontFamily: "inherit" }}>
                    {item.is_weight_based ? `${item.qty.toFixed(3)} ${item.unit || "kg"}` : `${item.qty}`} × ${item.price.toFixed(2)}
                  </Typography>
                </Box>
                <Typography fontSize={11} fontWeight={600} sx={{ fontFamily: "inherit" }}>${item.total.toFixed(2)}</Typography>
              </Box>
            ))}

            <Divider sx={{ my: 1, borderStyle: "dashed" }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}><span>Subtotal</span><span>${data.subTotal.toFixed(2)}</span></Box>
            {data.discount > 0 && <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#dc2626" }}><span>Discount</span><span>-${data.discount.toFixed(2)}</span></Box>}
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}><span>{data.taxLabel}</span><span>${data.taxAmount.toFixed(2)}</span></Box>
            <Divider sx={{ my: 1, borderStyle: "dashed" }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700 }}><span>TOTAL</span><span>${data.grandTotal.toFixed(2)}</span></Box>
            <Divider sx={{ my: 1, borderStyle: "dashed" }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}><span>{data.paymentMethod}</span><span>${data.amountPaid.toFixed(2)}</span></Box>
            {data.change > 0 && <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 700 }}><span>Change</span><span>${data.change.toFixed(2)}</span></Box>}
            <Typography textAlign="center" fontSize={10} color="#888" mt={1.5}>Thank you!</Typography>
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 1.5, p: 2.5, borderTop: "1px solid #e2e8f0" }}>
          <Button fullWidth variant="outlined" onClick={onClose}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b" }}>
            Close
          </Button>
          <Button fullWidth variant="contained" disableElevation startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
            onClick={handlePrint}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 700, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" } }}>
            Print
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export { buildReceiptHTML };
export default ReceiptPrinter;
